import React from 'react';
import { useParams } from 'react-router-dom';
import { ConfidenceIndicator } from '../../../components/indicators';
import { useSubmissionConfidence } from '../../../console/submissions/modules/_hooks/useSubmissionConfidence';
import { useUpdateConfidenceScores } from '../../../console/submissions/modules/_hooks/useUpdateConfidenceScores';

/**
 * The value the chip shows: the live fieldset's reading of the field when it has one, else the form's own score
 * (a percentage), else zero.
 */
export function confidenceChipValue(
  fieldsetValue: number | undefined,
  score: number | null | undefined
): number {
  return fieldsetValue ?? (score ?? 0) / 100;
}

interface FieldConfidenceChipProps {
  readonly fieldId: string;
  readonly formId: string;
  readonly confidenceScore?: number;
  /** Falls back to the route's submission id. */
  readonly submissionId?: string;
}

/** The confidence chip beside a field's label; clicking it confirms the answer, setting its confidence to 100. */
export function FieldConfidenceChip({
  fieldId,
  formId,
  confidenceScore,
  submissionId,
}: FieldConfidenceChipProps) {
  const params = useParams<{ submissionId?: string }>();
  const { fieldsets, handlers, locked } = useSubmissionConfidence();
  const { updateScores } = useUpdateConfidenceScores();
  const effectiveSubmissionId = submissionId ?? params.submissionId ?? '';

  const handleConfirm = () => {
    if (effectiveSubmissionId) {
      updateScores(effectiveSubmissionId, { [fieldId]: 100 }).then(() => {
        handlers.setFieldConfidence100(formId, fieldId);
      });
    }
  };

  return (
    <ConfidenceIndicator
      value={confidenceChipValue(
        fieldsets?.[formId]?.[fieldId],
        confidenceScore
      )}
      onClick={locked ? undefined : handleConfirm}
      {...(locked && { disabled: true })}
    />
  );
}
